import React from 'react';
import { Text } from '@nimbus-ds/components';
import { Card } from 'commons/services/refundReceiptService';
import { MoneyObjectInterface, PaymentMethodsType } from 'commons/types';
import {
  capitalize,
  isNotEmpty,
  useTranslationWithPrefix,
} from 'commons/utils';

interface PaymentMethodDetailProps {
  paymentMethod: PaymentMethodsType;
  installments: number;
  totalInterest: MoneyObjectInterface;
  card?: Card;
  paymentMethodBrand?: string;
}

function PaymentMethodDetail({
  paymentMethod,
  installments,
  totalInterest,
  card,
  paymentMethodBrand,
}: Readonly<PaymentMethodDetailProps>) {
  const { t } = useTranslationWithPrefix('refundReceipt.paymentDetail');

  const detailText = (children: React.ReactNode) => (
    <Text
      fontSize="base"
      fontWeight="regular"
      color="neutral-textLow"
      textAlign="left"
    >
      {children}
    </Text>
  );

  const cardInfo = card?.lastFourDigits
    ? detailText(
        t('card', {
          brand: card?.brand ? capitalize(card.brand) : '',
          lastFourDigits: card.lastFourDigits,
        }),
      )
    : null;

  const installmentsInfo =
    installments >= 1
      ? detailText(
          `${t('installments', {
            installments,
            count: installments,
          })} ${
            totalInterest?.value ? t('withInterest') : t('withoutInterest')
          }`,
        )
      : null;

  const hasCardData = isNotEmpty(card);

  switch (paymentMethod) {
    case 'debit_card':
    case 'credit_card':
      return (
        <>
          {cardInfo}
          {installmentsInfo}
        </>
      );

    case 'wire_transfer':
      return detailText(t('wireTransfer'));

    case 'ticket':
      return detailText(
        t('ticket', {
          brand: paymentMethodBrand ? capitalize(paymentMethodBrand) : '',
        }),
      );

    case 'wallet':
      return (
        <>
          {detailText(t('wallet'))}
          {hasCardData && cardInfo}
          {hasCardData && installmentsInfo}
        </>
      );

    default:
      return null;
  }
}

export default PaymentMethodDetail;
